import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { SchoolRounded, FlagRounded, DescriptionRounded } from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentProgramEnrollments, useDegreeAudit } from '../hooks/use-portal-data';
import { PortalLoading, PortalEmpty } from '../PortalShell';
import type {
  ProgramEnrollmentDto,
  ProgramEnrollmentStatus,
  DegreeAuditDto,
} from '../../../lib/api/program-enrollment.api';

const CARD_SX = {
  bgcolor: '#161b22',
  border: '1px solid #21262d',
  borderRadius: 2,
} as const;

const STATUS_COLOR: Record<ProgramEnrollmentStatus, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  on_leave: 'warning',
  graduated: 'info',
  withdrawn: 'default',
};

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card sx={CARD_SX} elevation={0}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#8b949e', fontWeight: 700, letterSpacing: 1 }}>
          {title}
        </Typography>
        <Box sx={{ mt: 1.5 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export function CareerPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const enrollmentsQ = useStudentProgramEnrollments();
  const enrollments = useMemo(() => enrollmentsQ.data?.items ?? [], [enrollmentsQ.data]);

  // Name maps for programs + terms (audit/enrollment carry ids only).
  const [programNames, setProgramNames] = useState<Map<string, string>>(new Map());
  const [termNames, setTermNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    Promise.all([
      getDocs(collection(db, `tenants/${currentTenantId}/programs`)),
      getDocs(collection(db, `tenants/${currentTenantId}/terms`)),
    ]).then(([progSnap, termSnap]) => {
      if (!alive) return;
      setProgramNames(
        new Map(progSnap.docs.map((d) => [d.id, (d.data()['name'] as string) ?? d.id])),
      );
      setTermNames(new Map(termSnap.docs.map((d) => [d.id, (d.data()['name'] as string) ?? d.id])));
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  // Selected program: default to first active, else first.
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  useEffect(() => {
    if (selectedProgramId || enrollments.length === 0) return;
    const active = enrollments.find((e) => e.status === 'active');
    setSelectedProgramId((active ?? enrollments[0]!).programId);
  }, [enrollments, selectedProgramId]);

  const auditQ = useDegreeAudit(selectedProgramId || undefined);

  if (enrollmentsQ.isLoading) return <PortalLoading />;

  if (enrollments.length === 0) {
    return (
      <PortalEmpty
        icon={<SchoolRounded sx={{ fontSize: 48 }} />}
        title={t('career.empty.noEnrollment.title')}
        body={t('career.empty.noEnrollment.body')}
      />
    );
  }

  const selected = enrollments.find((e) => e.programId === selectedProgramId);
  const programLabel = (e: ProgramEnrollmentDto) => programNames.get(e.programId) ?? e.programId;
  const termLabel = (id: string | null) => (id ? (termNames.get(id) ?? id) : null);

  return (
    <Stack spacing={2.5} sx={{ pb: 4 }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        useFlexGap
      >
        <Box>
          <Typography variant="h4" sx={{ color: '#e6edf3', fontWeight: 700 }}>
            {t('career.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#8b949e' }}>
            {t('career.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DescriptionRounded />}
          onClick={() => navigate('/portal-v2/historial/transcript')}
          sx={{ color: '#c9d1d9', borderColor: '#30363d' }}
        >
          {t('career.viewTranscript')}
        </Button>
      </Stack>

      {enrollments.length > 1 && (
        <FormControl size="small" sx={{ maxWidth: 360 }}>
          <InputLabel sx={{ color: '#8b949e' }}>{t('career.selectProgram')}</InputLabel>
          <Select
            label={t('career.selectProgram')}
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(e.target.value)}
            sx={{ color: '#e6edf3' }}
          >
            {enrollments.map((e) => (
              <MenuItem key={e.programId} value={e.programId}>
                {programLabel(e)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selected && (
        <SectionCard title={t('career.header.title')}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ color: '#e6edf3' }}>
              {programLabel(selected)}
            </Typography>
            <Chip
              size="small"
              color={STATUS_COLOR[selected.status]}
              label={t(`career.status.${selected.status}`)}
            />
            {selected.catalogYear && (
              <Chip
                size="small"
                variant="outlined"
                label={t('career.catalogYear', { year: selected.catalogYear })}
                sx={{ color: '#8b949e', borderColor: '#30363d' }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {termLabel(selected.intakeTermId) && (
              <Typography variant="body2" sx={{ color: '#8b949e' }}>
                {t('career.intakeTerm')}:{' '}
                <b style={{ color: '#c9d1d9' }}>{termLabel(selected.intakeTermId)}</b>
              </Typography>
            )}
            {termLabel(selected.expectedGraduationTermId) && (
              <Typography variant="body2" sx={{ color: '#8b949e' }}>
                {t('career.expectedGraduation')}:{' '}
                <b style={{ color: '#c9d1d9' }}>{termLabel(selected.expectedGraduationTermId)}</b>
              </Typography>
            )}
          </Stack>
        </SectionCard>
      )}

      {auditQ.isLoading && <PortalLoading />}

      {auditQ.data && <AuditBody audit={auditQ.data} t={t} />}

      {!auditQ.isLoading && !auditQ.data && auditQ.isError && (
        <PortalEmpty
          title={t('career.empty.noCurriculum.title')}
          body={t('career.empty.noCurriculum.body')}
        />
      )}
    </Stack>
  );
}

function AuditBody({
  audit,
  t,
}: {
  audit: DegreeAuditDto;
  t: ReturnType<typeof useTranslation>['t'];
}): JSX.Element {
  const pendingByTerm = useMemo(() => {
    const map = new Map<number, DegreeAuditDto['pending']>();
    for (const p of audit.pending) {
      const arr = map.get(p.recommendedTermOffset) ?? [];
      arr.push(p);
      map.set(p.recommendedTermOffset, arr);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [audit.pending]);

  const hasCurriculum =
    audit.creditsRequired > 0 || audit.pending.length > 0 || audit.completed.length > 0;

  if (!hasCurriculum) {
    return (
      <PortalEmpty
        title={t('career.empty.noCurriculum.title')}
        body={t('career.empty.noCurriculum.body')}
      />
    );
  }

  return (
    <>
      <SectionCard title={t('career.progress.title')}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h3" sx={{ color: '#e6edf3', fontWeight: 700 }}>
              {audit.percentComplete}%
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {t('career.progress.credits', {
                earned: audit.creditsEarned,
                required: audit.creditsRequired,
              })}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, audit.percentComplete))}
            sx={{ height: 8, borderRadius: 4, bgcolor: '#21262d' }}
          />
        </Stack>
      </SectionCard>

      <SectionCard title={t('career.recommended.title')}>
        {audit.recommendedNextTerm.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#6e7681' }}>
            {t('career.recommended.empty')}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {audit.recommendedNextTerm.map((c) => (
              <Chip
                key={c.courseId}
                icon={<FlagRounded />}
                label={c.courseName}
                sx={{ bgcolor: 'rgba(99,102,241,0.18)', color: '#e6edf3' }}
              />
            ))}
          </Stack>
        )}
      </SectionCard>

      <SectionCard title={t('career.pending.title')}>
        {pendingByTerm.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#6e7681' }}>
            {t('career.pending.empty')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {pendingByTerm.map(([offset, courses]) => (
              <Box key={offset}>
                <Typography variant="caption" sx={{ color: '#8b949e', fontWeight: 700 }}>
                  {t('career.pending.term', { n: offset + 1 })}
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  {courses.map((c) => (
                    <Stack
                      key={c.courseId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" sx={{ color: '#c9d1d9' }}>
                        {c.courseName}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {!c.prerequisitesMet && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={t('career.pending.prereqMissing')}
                          />
                        )}
                        <Typography variant="caption" sx={{ color: '#6e7681' }}>
                          {t('career.credits', { credits: c.credits })}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      <SectionCard title={t('career.completed.title')}>
        {audit.completed.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#6e7681' }}>
            {t('career.completed.empty')}
          </Typography>
        ) : (
          <Stack spacing={0.75} divider={<Divider sx={{ borderColor: '#21262d' }} />}>
            {audit.completed.map((c) => (
              <Stack
                key={c.courseId}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" sx={{ color: '#c9d1d9' }}>
                  {c.courseName}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {c.grade !== null && (
                    <Typography variant="caption" sx={{ color: '#8b949e' }}>
                      {t('career.completed.grade', { grade: c.grade })}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: '#6e7681' }}>
                    {t('career.credits', { credits: c.credits })}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      {audit.offTrack.length > 0 && (
        <SectionCard title={t('career.offTrack.title')}>
          <Stack spacing={0.75}>
            {audit.offTrack.map((c) => (
              <Typography key={c.courseId} variant="body2" sx={{ color: '#c9d1d9' }}>
                {c.courseName}
              </Typography>
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: '#6e7681', mt: 1, display: 'block' }}>
            {t('career.offTrack.note')}
          </Typography>
        </SectionCard>
      )}
    </>
  );
}
